import {Component, Inject, OnDestroy, PLATFORM_ID, ViewChild} from '@angular/core';
import {GoogleMap, MapAdvancedMarker, MapInfoWindow} from '@angular/google-maps';
import {Subject, takeUntil} from 'rxjs';
import {MapService, MapState} from '../../../services/map.service';
import {environment} from '../../../environments/environment';
import {Place} from '../../../models/place';
import {isPlatformBrowser, NgForOf, NgIf} from '@angular/common';
import {AuthService} from '../../../services/auth.service';
import {User} from '../../../models/user';
import {Room} from '../../../models/room';
import {RoomRectangleComponent} from '../room-rectangle/room-rectangle.component';
import LatLngBounds = google.maps.LatLngBounds;

@Component({
  selector: 'app-room-map',
  imports: [
    GoogleMap,
    MapAdvancedMarker,
    MapInfoWindow,
    NgForOf,
    RoomRectangleComponent,
    NgIf
  ],
  templateUrl: './room-map.component.html',
  styleUrl: './room-map.component.scss'
})
export class RoomMapComponent implements OnDestroy {
  onDestroy$ = new Subject<boolean>();

  GOOGLE_MAP_ID = environment.GOOGLE_MAP_ID

  center: google.maps.LatLng | google.maps.LatLngLiteral = {lat: 0, lng: 0}
  zoom = 10

  markers: any[] = []

  // User
  currentUser: User | null = null

  // Place
  place: Place | null = null

  // Rooms
  rooms: Room[] = []

  // No bound - Region, Place, Conference Room
  // Soft bound - Neighborhood
  // Hard bound - Geolocation (Forced once enabled: Region, Neighborhood, Place, Conference Room)
  isEnforcingHardBound = false;

  @ViewChild(GoogleMap) googleMap!: GoogleMap;
  @ViewChild(MapInfoWindow) mapInfoWindow!: MapInfoWindow;

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
              private authService: AuthService,
              private mapService: MapService) {
    this.authService.currentUser$.pipe(takeUntil(this.onDestroy$)).subscribe(currentUser => {
      if (currentUser) this.currentUser = currentUser
    });

    this.mapService.mapState$.pipe(takeUntil(this.onDestroy$)).subscribe(mapState => {
      if (mapState) this.handleMapStateChange(mapState).finally()
    })

  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  async handleMapStateChange(mapState: MapState) {
    if (isPlatformBrowser(this.platformId)) {

      // Close any existing map info window
      if (this.mapInfoWindow) this.mapInfoWindow.close()

      // @ts-ignore
      const {LatLngBounds} = await google.maps.importLibrary("core")

      // Customized marker pin
      this.markers = mapState.marks
      this.markers.forEach(marker => {
        const pin = document.createElement('img');
        pin.width = 34
        pin.height = 34
        pin.src = '/images/conference-room/room-map/marker-location-open.png'

        // Override: New users should be able to view the pin as active instead of inactive to drive higher conversion
        if (!this.currentUser) {
          pin.src = '/images/conference-room/room-map/marker-location-open.png'
        }

        marker.content = pin
      })

      // Hard bound (Forced once it's used once)
      if (this.isEnforcingHardBound || mapState.hardBound) {

        let bounds = new LatLngBounds();
        this.markers.forEach(marker => {
          bounds.extend({lat: marker.latitude, lng: marker.longitude});
        })

        // Adding center for geolocation
        if (mapState.hardBound) {
          bounds.extend({lat: mapState.latitude, lng: mapState.longitude})
        }

        // Set bounds (sometimes googleMap is loaded much slower)
        if (this.googleMap) this.googleMap.fitBounds(bounds, 100)

        // Enforcing hard bound
        this.isEnforcingHardBound = true;
      }

      if (!this.isEnforcingHardBound) {

        // Set center (default)
        if (!mapState.softBound) {
          this.center = {lat: mapState.latitude, lng: mapState.longitude}
          this.zoom = mapState.zoom
        }

        // Set bounds
        if (mapState.softBound) {
          let bounds = new LatLngBounds();
          this.markers.forEach(marker => {
            bounds.extend({lat: marker.latitude, lng: marker.longitude});
          })

          this.center = bounds.getCenter()
          this.zoom = this.getBoundsZoomLevel(bounds)
        }
      }
    }
  }

  onMarkerClick(marker: MapAdvancedMarker, place: Place, rooms: Room[]) {
    this.place = place
    this.rooms = rooms
    this.mapInfoWindow.open(marker);
  }

  onMapClick() {
    if (this.mapInfoWindow) this.mapInfoWindow.close();
  }

  getBoundsZoomLevel(bounds: LatLngBounds) {
    const WORLD_DIM = {height: 256, width: 256};
    const ZOOM_MAX = 18;

    function latRad(lat: number) {
      const sin = Math.sin(lat * Math.PI / 180);
      return Math.log((1 + sin) / (1 - sin)) / 2;
    }

    function zoom(mapPx: number, worldPx: number, fraction: number) {
      return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
    }

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    // Calculate lat/lng fractions
    const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;
    const lngDiff = ne.lng() - sw.lng();
    const lngFraction = ((lngDiff < 0) ? (lngDiff + 360) : lngDiff) / 360;

    // Calculate map HTML element height and width
    let mapHeight = 600
    let mapWidth = 400
    if (document) {
      const mapElement = document.getElementById('map');
      mapHeight = mapElement ? mapElement.offsetHeight : mapHeight
      mapWidth = mapElement ? mapElement.offsetWidth : mapWidth
    }

    // Calculate the zoom limits
    const latZoom = zoom(mapHeight, WORLD_DIM.height, latFraction);
    const lngZoom = zoom(mapWidth, WORLD_DIM.width, lngFraction);

    return Math.min(latZoom, lngZoom, ZOOM_MAX)
  }


}
